import {
  EventEmitter,
  Injectable,
  OnInit,
  Optional,
  SkipSelf
} from '@angular/core'
import { ActivatedRouteSnapshot, Router } from '@angular/router'
import { LangChangeEvent, TranslateService } from '@ngx-translate/core'
import { noop, of } from 'rxjs'
import localeEs from '@angular/common/locales/es'
import localeFr from '@angular/common/locales/fr'
import localeEn from '@angular/common/locales/en'
import localePt from '@angular/common/locales/pt'
import localeZhCn from '@angular/common/locales/zh-Hans'
import localeSk from '@angular/common/locales/sk'
import { registerLocaleData } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { forkJoin } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

/*
type ShouldReuseRoute = (
  future: ActivatedRouteSnapshot,
  curr: ActivatedRouteSnapshot
) => boolean
*/

@Injectable({
  providedIn: 'root'
})
export class LocaleService implements OnInit {
  private initialized = false
  private _locale: string
  private language: string
  private reload: boolean
  private availableLanguages = [
    'en',
    'de',
    'sk',
    'fr',
    'es',
    'pt-br',
    'zh-cn',
    'he'
  ]

  get currentLocale(): string {
    return this._locale
  }
  setLocaleValue(value: string) {
    this._locale = value
  }
  private modules = [
    '/assets/i18n/auth',
    '/assets/i18n/org',
    '/assets/i18n/user',
    '/assets/i18n/admin',
    '/assets/i18n/kpi',
    '/assets/i18n/art',
    '/assets/i18n/pi',
    '/assets/i18n/portfolio',
    '/assets/i18n/profile',
    '/assets/i18n/signup',
    '/assets/i18n/solutions',
    '/assets/i18n/team',
    '/assets/i18n/va'
    // add all your translation folders here
  ]

  // eslint-disable-next-line @typescript-eslint/member-ordering
  constructor(
    private router: Router,
    private translate: TranslateService,
    private http: HttpClient,
    @Optional()
    @SkipSelf()
    otherInstance: LocaleService
  ) {
    // eslint-disable-next-line no-throw-literal
    if (otherInstance) throw 'LocaleService should have only one instance.'
    const navigatorLanguage = navigator.language.substring(2, 0)
    if (this.availableLanguages.includes(navigatorLanguage)) {
      this.initLocale(navigatorLanguage)
    } else {
      this.initLocale('en')
    }
    this.setLocaleData(this.currentLocale)
  }
  ngOnInit(): void {}
  /*
  private setRouteReuse(reuse: ShouldReuseRoute) {
    this.router.routeReuseStrategy.shouldReuseRoute = reuse
  }
  */

  private subscribeToLangChange() {
    this.translate.onLangChange.subscribe(({ lang }) => {
      //  const { shouldReuseRoute } = this.router.routeReuseStrategy
      this.language = lang
      if (this.currentLocale !== this.language) {
        this.setLocale(this.language)
      }

      /* const currentRoute = this.router.url
      console.log("current",currentRoute)
      await this.router
        .navigateByUrl('organizations')
        .catch(noop)
        .then(() => this.setRouteReuse(shouldReuseRoute))
        .finally(() => this.router.navigateByUrl(currentRoute))*/
    })
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  initLocale(localeId: string, defaultLocaleId = localeId) {
    if (this.initialized) return

    this.setDefaultLocale(defaultLocaleId)
    this.setLocale(localeId)
    this.subscribeToLangChange()

    this.initialized = true
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  setDefaultLocale(localeId: string) {
    this.translate.setDefaultLang(localeId)
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  setLocale(localeId: string) {
    this.setLocaleData(localeId)
    this.setLocaleValue(localeId)
    this.loadAllTranslations(localeId)
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  setLocaleData(localeId: string) {
    switch (localeId) {
      case 'es':
        registerLocaleData(localeEs)

        break
      case 'en':
        registerLocaleData(localeEn)
        break
      case 'fr':
        registerLocaleData(localeFr)
        break
      case 'pt-BR':
        registerLocaleData(localePt)
        break
      case 'zh-cn':
        registerLocaleData(localeZhCn, 'zh-cn')
        break
      case 'sk':
        registerLocaleData(localeSk)
        break

      default:
        break
    }
  }

  loadAllTranslations(lang: string) {
    const requests = this.modules.map((path) =>
      this.http.get(`${path}/${lang}.json`, { responseType: 'text' }).pipe(
        map((text) => {
          try {
            return JSON.parse(text)
          } catch {
            throw new Error(`Invalid JSON in ${path}/${lang}.json`)
          }
        }),
        catchError((err) => {
          console.warn(
            `No se pudo cargar ${path}/${lang}.json, se cargará idioma por defecto en su lugar`
          )
          // Try default language as text first
          return this.http
            .get(`${path}/en.json`, { responseType: 'text' })
            .pipe(
              map((text) => {
                try {
                  return JSON.parse(text)
                } catch {
                  return {}
                }
              }),
              catchError(() => of({}))
            )
        })
      )
    )

    forkJoin(requests).subscribe({
      next: (results) => {
        results.forEach((translations) => {
          this.translate.setTranslation(lang, translations, true)
        })
        this.translate.use(lang)
      },
      error: (err) => {
        // Log any unexpected errors
        console.error('Error loading translations:', err)
      }
    })
  }
}
